// ReferralView — tela "Indicar amigos" do aluno.
//
// Mostra:
//   - Código de 6 chars + link compartilhável (cópia 1-clique)
//   - Botões de compartilhamento (WhatsApp, copiar link, copiar código)
//   - Contadores: total indicados, total que viraram pagantes
//   - Lista dos indicados (display name + status + se já pagou)
//
// O servidor NUNCA devolve email/id dos indicados — só nome social/nome
// completo (o que o aluno aceitou usar) + flags.

import { useEffect, useState } from "preact/hooks";
import {
  Check,
  Copy,
  Gift,
  Loader2,
  MessageCircle,
  Share2,
  Sparkles,
  Users
} from "lucide-react";
import { api } from "./api";

interface ReferralData {
  code: string;
  link: string;
  totalReferred: number;
  totalActive: number;
  indicated: Array<{
    /** Nome mascarado tipo "Maria S." — backend nunca devolve nome completo
     *  nem email pra esta view. Privacidade dos indicados. */
    displayName: string;
    /** Apenas mês/ano ("Mai/26") — sem dia exato pra dificultar fingerprint. */
    createdAtMonth: string;
    suspended: boolean;
    /** True = indicado já está engajado (concluiu jornada). Não revelamos
     *  ao indicador que houve pagamento — só que a conta tá ativa. */
    active: boolean;
  }>;
}

export function ReferralView() {
  const [data, setData] = useState<ReferralData | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState<"link" | "code" | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setPhase("loading");
      try {
        const r = await api.referral();
        if (cancelled) return;
        setData(r);
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setErrMsg(e instanceof Error ? e.message : String(e));
        setPhase("error");
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function copyText(text: string, what: "link" | "code") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // ignora — algumas plataformas exigem permissão explícita
    }
  }

  function shareWhatsApp() {
    if (!data) return;
    const msg = `Te indico o Simulador (plataforma de estudos pra concursos/vestibulares). Use meu código ${data.code} no cadastro: ${data.link}`;
    const url = `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  }

  async function nativeShare() {
    if (!data || !navigator.share) return;
    try {
      await navigator.share({
        title: "Te indico o Simulador",
        text: `Use meu código ${data.code} no cadastro:`,
        url: data.link
      });
    } catch {
      /* user cancelou */
    }
  }

  if (phase === "loading") {
    return (
      <div class="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="mr-2 size-4 animate-spin" /> Carregando…
      </div>
    );
  }

  if (phase === "error" || !data) {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        Erro ao carregar indicações: {errMsg ?? "tente recarregar"}.
      </div>
    );
  }

  return (
    <div class="space-y-6">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Gift className="size-6 text-violet-600" />
          Indicar amigos
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Compartilhe seu código pra trazer outros estudantes. A gente registra
          tudo — futuramente teremos um programa de bonificação pra quem
          ajudar a crescer a comunidade.
        </p>
      </header>

      {/* Card principal: código + link */}
      <div class="rounded-md border border-violet-500/30 bg-gradient-to-br from-violet-500/10 to-fuchsia-500/5 p-6">
        <div class="grid gap-6 md:grid-cols-2">
          <div>
            <div class="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              Seu código
            </div>
            <div class="mt-2 flex items-center gap-2">
              <span class="font-mono text-4xl font-bold tracking-[0.25em] text-foreground">
                {data.code}
              </span>
              <button
                type="button"
                onClick={() => copyText(data.code, "code")}
                class="grid size-9 place-items-center rounded-md border border-border bg-background hover:bg-accent"
                title="Copiar código"
                aria-label="Copiar código"
              >
                {copied === "code" ? (
                  <Check className="size-4 text-emerald-600" />
                ) : (
                  <Copy className="size-4" />
                )}
              </button>
            </div>
          </div>

          <div>
            <div class="text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              Link compartilhável
            </div>
            <div class="mt-2 flex items-center gap-2">
              <input
                type="text"
                readOnly
                aria-label="Link compartilhável"
                title="Link compartilhável"
                value={data.link}
                onClick={(e) => (e.target as HTMLInputElement).select()}
                class="min-w-0 flex-1 truncate rounded-md border border-border bg-background px-3 py-1.5 text-xs"
              />
              <button
                type="button"
                onClick={() => copyText(data.link, "link")}
                class="grid size-9 shrink-0 place-items-center rounded-md border border-border bg-background hover:bg-accent"
                title="Copiar link"
                aria-label="Copiar link"
              >
                {copied === "link" ? (
                  <Check className="size-4 text-emerald-600" />
                ) : (
                  <Copy className="size-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div class="mt-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={shareWhatsApp}
            class="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700"
          >
            <MessageCircle className="size-4" />
            WhatsApp
          </button>
          {typeof navigator !== "undefined" && (navigator as any).share && (
            <button
              type="button"
              onClick={nativeShare}
              class="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-accent"
            >
              <Share2 className="size-4" />
              Compartilhar…
            </button>
          )}
        </div>
      </div>

      {/* Contadores */}
      <div class="grid gap-3 sm:grid-cols-2">
        <div class="rounded-md border border-border bg-card p-4">
          <div class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            <Users className="size-3.5" />
            Indicados
          </div>
          <div class="mt-1 text-3xl font-bold tabular-nums">
            {data.totalReferred}
          </div>
          <div class="text-xs text-muted-foreground">amigos no Simulador</div>
        </div>
        <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            <Sparkles className="size-3.5" />
            Ativos
          </div>
          <div class="mt-1 text-3xl font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
            {data.totalActive}
          </div>
          <div class="text-xs text-muted-foreground">concluíram a jornada de cadastro</div>
        </div>
      </div>

      {/* Lista */}
      <div>
        <h2 class="mb-2 text-sm font-bold">Histórico de indicados</h2>
        {data.indicated.length === 0 ? (
          <div class="rounded-md border border-dashed border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground">
            Ninguém ainda. Compartilhe seu código e fica de olho aqui.
          </div>
        ) : (
          <>
            <ul class="divide-y divide-border rounded-md border border-border bg-card">
              {data.indicated.map((u, i) => (
                <li key={i} class="flex items-center justify-between gap-3 px-4 py-3">
                  <div class="min-w-0">
                    <div class="truncate text-sm font-medium">{u.displayName}</div>
                    <div class="text-[11px] text-muted-foreground">
                      Entrou em {u.createdAtMonth}
                    </div>
                  </div>
                  <div class="flex shrink-0 items-center gap-2">
                    {u.active && (
                      <span class="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300">
                        Ativo
                      </span>
                    )}
                    {u.suspended && (
                      <span class="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">
                        Suspenso
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            <p class="mt-2 text-[11px] text-muted-foreground">
              Por privacidade, mostramos só o primeiro nome + inicial e o mês
              de entrada dos indicados.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
